// #region imports

// --- Angular
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

// --- RxJs
import {
  Observable,
  BehaviorSubject,
  Subscription,
  combineLatest,
} from 'rxjs';
import { switchMap, filter, take, map, distinctUntilChanged, tap, debounceTime, takeWhile } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { NgbAlert, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { User as OidcUser } from 'oidc-client';
import moment from 'moment';

// --- HS Code
import { CompareReportModalComponent } from '../../components/compare-report-modal/compare-report-modal.component';
import { CompareResultsInfoComponent } from '../../components/compare-results-info/compare-results-info.component';
import { ManageRespondentsShellComponent } from '../manage-respondents-shell/manage-respondents-shell.component';
import { ResultsHintModalComponent } from '../../components/results-hint-modal/results-hint-modal.component';
import { SelfRegistrationShellComponent } from '../self-registration-shell/self-registration-shell.component';
import {
  AlertModel,
  ChartClickResponse,
  Constants,
  GroupModel,
  InvitationTypes,
  ParticipantValidationDestinations,
  ReportRequestModel,
  RespondentModel,
  RespondentProgressModel,
  ResultsViews,
  SavedReportTypes,
  ScoreModels,
  SortDirections,
  SurveyResponseModel,
  SurveyResponseTypes,
  SurveyStatuses,
  SurveySummaryBasicModel,
  SurveySummaryModel,
  TrackMyJourneyResultsModel,
  UserDetailsModel,
} from '../../../models';
import {
  AppService,
  CompareReportBaseService,
  LanguagesBaseService,
  OidcService,
  ParticipantGroupsBaseService,
  ReportsBaseService,
  RespondentReminderBaseService,
  ResultsBaseService,
  ProfileBaseService,
  SelfRegistrationBaseService,
  SurveyBaseService,
  FeaturesService,
  ManageRespondentsService,
  ResponseBaseService,
} from '../../../core/services';
import { ManageRespondentsShellV2Component } from '../manage-respondents-shell-v2/manage-respondents-shell-v2.component';
import { SurveySummaryBottomSheetComponent } from '../../components/survey-summary-bottom-sheet/survey-summary-bottom-sheet.component';
import { SurveySummariesBottomSheetOutput } from '../../components/survey-summary-bottom-sheet/survey-summary-bottom-sheet.model';


// #endregion

declare let gtag: Function;

@Component({
  selector: 'hs-dashboard-home',
  templateUrl: './dashboard-home.component.html',
  styleUrls: ['./dashboard-home.component.scss'],
})
export class DashboardHomeComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('selfClosingAlert', { static: false }) manageRespondentsAlert: NgbAlert | undefined;

  private _isShowingSelfRegistration: boolean = false;

  identityUser: OidcUser | null = null;
  openPacksFeatureEnabled: boolean = false;
  selectedSelfSurvey: SurveySummaryModel | null = null;
  alert: AlertModel | null = null;

  navigationState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  respondentsExpanded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );
  showRespondentsReminder$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  areResultsLoading$: Observable<boolean> | undefined;
  compareReportDisplayMode$: Observable<ResultsViews> | undefined;
  compareReportResults$:
    | Observable<TrackMyJourneyResultsModel[] | null>
    | undefined;
  compareReportResultsLoading$: Observable<boolean> | undefined;
  errorMessage$: Observable<string | null> | undefined;
  identity$: Observable<OidcUser | null> | undefined;
  imagesRootUrl$: Observable<string> | undefined;
  isAdding$: Observable<boolean> | undefined;
  isLoading$: Observable<boolean> | undefined;
  reportIsDownLoading$: Observable<boolean> | undefined;
  reportIsLoading$: Observable<boolean> | undefined;
  respondentGroups$: Observable<GroupModel[]> | undefined;
  respondentProgress$: Observable<RespondentProgressModel | null> | undefined;
  response$: Observable<SurveyResponseModel | null> | undefined;
  resultsDisplayMode$: Observable<ResultsViews> | undefined;
  scoreList$: Observable<ScoreModels | null> | undefined;
  selfSurveys$: Observable<SurveySummaryModel[] | null> | undefined;
  shouldShowSelfRegistration$: Observable<boolean> | undefined;
  surveyNameMaxLengthMessage$: Observable<string> | undefined;
  surveyNameRequiredMessage$: Observable<string> | undefined;
  userDetails$: Observable<UserDetailsModel | null> | undefined;

  selectedSelfSurvey$: Observable<SurveySummaryModel | null>;
  showCompareResultsModal$: Observable<boolean>;

  isMobile$: Observable<boolean>;

  private _identitySubscription: Subscription = Subscription.EMPTY;
  private _manageRespondentsAlertSubscription: Subscription = Subscription.EMPTY;
  private _mobileUserClaimSubscription: Subscription = Subscription.EMPTY;
  private _openPacksFeatureStateSubscription: Subscription = Subscription.EMPTY;
  private _openSurveySummariesSubscription?: Subscription = Subscription.EMPTY;
  private _selectedSurveySubscription: Subscription = Subscription.EMPTY;
  private _selfRegistrationOpenSubscription: Subscription = Subscription.EMPTY;
  private _showCompareResultsModalSubscription: Subscription = Subscription.EMPTY;
  private _showManageRespondentsModalSubscription: Subscription = Subscription.EMPTY;
  private _surveyAndResultsSubscription: Subscription = Subscription.EMPTY;
  private _surveyRedirectSubscription: Subscription = Subscription.EMPTY;

  manageRespondentsAlertMessage: string = '';

  constructor(
    private _appService: AppService,
    private _bottomSheet: MatBottomSheet,
    private _breakpointObserver: BreakpointObserver,
    private _compareReportService: CompareReportBaseService,
    private _featuresService: FeaturesService,
    private _languageService: LanguagesBaseService,
    private _modalService: NgbModal,
    private _manageRespondentsService: ManageRespondentsService,
    private _oidcService: OidcService,
    private _participantGroupService: ParticipantGroupsBaseService,
    private _profileService: ProfileBaseService,
    private _reportService: ReportsBaseService,
    private _respondentReminderService: RespondentReminderBaseService,
    private _responseService: ResponseBaseService,
    private _resultsService: ResultsBaseService,
    private _router: Router,
    private _selfRegistrationService: SelfRegistrationBaseService,
    private _surveyService: SurveyBaseService,
    private _translate: TranslateService,
  ) {
    this.showRespondentsReminder$ = new BehaviorSubject(true);

    this.selectedSelfSurvey$ = this._surveyService.selectedSelfSurvey$;
    this.showCompareResultsModal$ =
      this._compareReportService.showCompareResultsModal$;
    this._openPacksFeatureStateSubscription = this._featuresService.openPacksFeatureState$.subscribe((featureState: string) => {
      this.openPacksFeatureEnabled = featureState.toLowerCase() === Constants.FEATURE_ON.toLowerCase();
    });

    this.isMobile$ = this._breakpointObserver.observe([Constants.MOBILE_MEDIA_QUERY])
      .pipe(
        distinctUntilChanged(),
        map((state) => state.matches),
      );
  }

  ngOnInit() {
    this.areResultsLoading$ = this._resultsService.areResultsLoading$;
    this.errorMessage$ = this._surveyService.surveyError$;
    this.isAdding$ = this._surveyService.isAdding$;
    this.isLoading$ = this._appService.isLoading$;
    this.reportIsDownLoading$ = this._reportService.reportIsDownloading$;
    this.reportIsLoading$ = this._reportService.reportIsLoading$;
    this.respondentGroups$ = this._participantGroupService.participantGroups$;
    this.respondentProgress$ = this._participantGroupService.respondentProgress$;
    this.response$ = this._responseService.activeResponse$;
    this.resultsDisplayMode$ = this._resultsService.widgetDisplayMode$;
    this.scoreList$ = this._resultsService.scoreList$;
    this.selfSurveys$ = this._surveyService.selfSurveys$;
    this._surveyService.loadSummaries(Constants.MAXIMUM_NUMBER_OF_SELF_SURVEYS, 1, '', '', SortDirections.descending, true);
    this.shouldShowSelfRegistration$ = this._selfRegistrationService.isSelfRegistrationEnabled$;
    this.userDetails$ = this._profileService.userDetails$;
    this.compareReportResults$ = this._compareReportService.surveyResults$;
    this.compareReportDisplayMode$ = this._compareReportService.widgetDisplayMode$;
    this.compareReportResultsLoading$ = this._compareReportService.areResultsLoading$;
    this.identity$ = this._oidcService.identity$;

    this._manageRespondentsAlertSubscription = this._manageRespondentsService.alerts$
      .pipe(
        takeWhile(m => true),
        tap((alertMessage: string) => (this.manageRespondentsAlertMessage = alertMessage)),
        debounceTime(Constants.DEFAULT_ALERT_TIMEOUT)
      )
      .subscribe(() => {
        this.manageRespondentsAlert?.close()
      });

    this.surveyNameRequiredMessage$ = this._translate.get('surveyName').pipe(
      switchMap((surveyName) =>
        this._translate.get('validationMessageFieldRequired', {
          value: surveyName,
        })
      )
    );

    this.surveyNameMaxLengthMessage$ = this._translate.get('surveyName').pipe(
      switchMap((surveyName) =>
        this._translate.get('validationMessageFieldMaxLength', {
          value: surveyName,
          x: Constants.SURVEY_NAME_MAXIMUM_LENGTH,
        })
      )
    );

    this.imagesRootUrl$ = this._languageService.selectedLanguage$.pipe(
      filter((language) => language !== null),
      map((language) => {
        const translateLanguageAndCulture =
          language.cultureCode !== null
            ? `${language.isoTwoLetterName}-${language.cultureCode}`
            : `${language.isoTwoLetterName}`;

        this._translate.use(translateLanguageAndCulture);
        return `/assets/images/i18n/${translateLanguageAndCulture}/`;
      })
    );

    this._selectedSurveySubscription =
      this._surveyService.selectedSelfSurvey$.subscribe((survey) => {
        this.selectedSelfSurvey = survey;
      });

    this._identitySubscription = this._oidcService.identity$.subscribe(
      (oidcUser: OidcUser | null) => {
        if (!oidcUser) return;
        this.identityUser = oidcUser;
      }
    );

    this._selfRegistrationOpenSubscription = this._selfRegistrationService.openSelfRegDlg.subscribe(() => this.onSelfRegisterButtonClicked())
  }

  ngAfterViewInit() {
    this._showCompareResultsModalSubscription =
      this.showCompareResultsModal$.subscribe((show: boolean) => {
        if (show) {
          document.body.classList.add('overflow-hidden');
          return;
        }

        document.body.classList.remove('overflow-hidden');
      });

    // automatically show the manage respondents modal if the participant redirect localstorage key has a value get local identity
    if (
      localStorage.getItem(Constants.PARTICIPANT_REDIRECT_DESTINATION_KEY) ===
      ParticipantValidationDestinations.managerespondents
    ) {
      this._showManageRespondentsModalSubscription = this.selectedSelfSurvey$
        .pipe(
          filter((selectedSurvey) => selectedSurvey !== null),
          take(1)
        )
        .subscribe((selectedSurvey) => {
          if (selectedSurvey) {
            this.onManageRespondentsButtonClicked(selectedSurvey.id);
          }
        });
    }
  }

  ngOnDestroy() {
    if (this._manageRespondentsAlertSubscription !== Subscription.EMPTY) {
      this._manageRespondentsAlertSubscription.unsubscribe();
      this._manageRespondentsAlertSubscription = Subscription.EMPTY;
    }
    if (this._openPacksFeatureStateSubscription !== Subscription.EMPTY) {
      this._openPacksFeatureStateSubscription.unsubscribe();
      this._openPacksFeatureStateSubscription = Subscription.EMPTY;
    }
    if (this._selectedSurveySubscription) {
      this._selectedSurveySubscription.unsubscribe();
      this._selectedSurveySubscription = Subscription.EMPTY;
    }
    if (this._showCompareResultsModalSubscription) {
      this._showCompareResultsModalSubscription.unsubscribe();
      this._showCompareResultsModalSubscription = Subscription.EMPTY;
    }
    if (this._surveyAndResultsSubscription) {
      this._surveyAndResultsSubscription.unsubscribe();
      this._surveyAndResultsSubscription = Subscription.EMPTY;
    }
    if (this._showManageRespondentsModalSubscription) {
      this._showManageRespondentsModalSubscription.unsubscribe();
      this._showManageRespondentsModalSubscription = Subscription.EMPTY;
    }
    if (this._identitySubscription) {
      this._identitySubscription.unsubscribe();
      this._identitySubscription = Subscription.EMPTY;
    }
    if (this._surveyRedirectSubscription) {
      this._surveyRedirectSubscription.unsubscribe();
      this._surveyRedirectSubscription = Subscription.EMPTY;
    }
    if (this._mobileUserClaimSubscription) {
      this._mobileUserClaimSubscription.unsubscribe;
      this._mobileUserClaimSubscription = Subscription.EMPTY;
    }
    if (this._selfRegistrationOpenSubscription && this._selectedSurveySubscription !== Subscription.EMPTY) {
      this._selfRegistrationOpenSubscription.unsubscribe();
      this._selfRegistrationOpenSubscription = Subscription.EMPTY;
    }

    if (this._openSurveySummariesSubscription) {
      this._openSurveySummariesSubscription.unsubscribe();
      this._openSurveySummariesSubscription = Subscription.EMPTY;
    }
  }

  onSurveyNameChanged(surveyName: string): void {
  }

  onStartContinueButtonClicked(survey: SurveySummaryModel) {
    (<any>window).Intercom('update', { hide_default_launcher: true });

    if (!survey) {
      return;
    }

    this._router.navigate(['/surveys', { id: survey.moniker || survey.id }]);
  }

  onToggleNavigationClick(isOpen: boolean): void {
    this.navigationState$.next(isOpen);
  }

  onCloseNavigation(): void {
    this.onToggleNavigationClick(false);
  }

  onNotificationClick(): void { }

  onSurveyClick(surveyIdentifier: string): void {
    this._surveyService.selectSelfSurvey(surveyIdentifier);
  }

  onLogout(): void {
    this._appService.setLoading(true);
    this._oidcService.signOutRedirect();
  }

  onResultsWidgetDisplayChanged(mode: ResultsViews): void {
    this._resultsService.setWidgetDisplayMode(mode);
  }

  onCompareResultsWidgetDisplayChanged(mode: ResultsViews): void {
    this._compareReportService.setWidgetDisplayMode(mode);
  }

  onToggleCompareResultsModal(show: boolean) {
    this._compareReportService.toggleCompareResultsModal(show);

    if (!show) {
      return;
    }

    this._surveyAndResultsSubscription = combineLatest([
      this._surveyService.selectedSelfSurvey$,
      this._resultsService.surveyResults$,
    ])
      .pipe(take(1))
      .subscribe(([selectedSurvey, results]) => {
        if (!selectedSurvey || !results) return;

        const modalRef = this._modalService.open(CompareReportModalComponent, {
          scrollable: true,
          centered: true,
          size: 'lg',
        });
        modalRef.componentInstance.selectedSurvey = selectedSurvey;
        modalRef.result
          .then((selectedCampaign: SurveySummaryModel) => {
            if (selectedCampaign && selectedSurvey) {
              this._compareReportService.compareResults(
                selectedSurvey.id,
                selectedCampaign.id
              );
            }
          })
          .catch((err) => { });
        this._compareReportService.setInitialResults(selectedSurvey, results);
      });
  }

  onDownloadReportClicked(identifier: string): void {
    this._reportService.getReportPdf(identifier, SavedReportTypes.pdg);
  }

  onOpenResultsHint() {
    this._modalService.open(ResultsHintModalComponent, { centered: true });
  }

  onManageRespondentsButtonClicked(identifier: string) {
    if (this.openPacksFeatureEnabled) {
      const modalRef = this._modalService.open(ManageRespondentsShellV2Component, {
        animation: true,
        backdrop: 'static',
        centered: true,
        keyboard: true,
        scrollable: true,
        size: 'lg',
        windowClass: 'manage-respondents-modal-v2 modal-fullscreen-md-down',
      });
    } else {
      const modalRef = this._modalService.open(ManageRespondentsShellComponent, {
        centered: true,
        scrollable: true,
        size: 'lg',
        windowClass: 'manage-respondents-modal modal-fullscreen-md-down',
      });
    }
  }

  onSelfRegisterButtonClicked() {
    if (this._isShowingSelfRegistration) return;

    this._isShowingSelfRegistration = true;

    const modalRef = this._modalService.open(SelfRegistrationShellComponent, {
      centered: true,
      scrollable: true,
      size: 'lg',
      windowClass: 'self-registration-modal',
    });

    modalRef.result.then((modalData: any) => {
      // onClose
      this._isShowingSelfRegistration = false;

      const action = modalData['action'];
      const id = modalData['id'];

      if (action === 'dashboard') {
        this._surveyService.selectSelfSurvey(id);
      } else {
        this._router.navigate(['/surveys', { id: id }]);
      }
    }, (reason) => {
      // onDismiss
      this._isShowingSelfRegistration = false;
      this._router.navigate(['/dashboard']);
    });
  }

  onToggleRespondents(expand: boolean) {
    this.respondentsExpanded$.next(expand);
  }

  onCloseReminder() {
    this.showRespondentsReminder$.next(false);
  }

  onSendReminder(respondent: RespondentModel) {
    this._respondentReminderService.reminderRequest({
      recipientId: respondent.id ?? '',
      senderId: respondent.participantId ?? '',
      invitationType: InvitationTypes.respondentReminder,
    });
  }

  onChartClicked(chartClickResponse: ChartClickResponse) {
    if (!chartClickResponse) {
      return;
    }
    if (!chartClickResponse.selectedSurvey) {
      return;
    }
    this._router.navigate([
      '/mydevelopment',
      this._getResponseTypeName(chartClickResponse.responseType),
      chartClickResponse.selectedSurvey.moniker,
    ]);
  }

  onDownloadTrackMyJourneyClicked(model: ReportRequestModel) {
    this._reportService.submitReportRequest(model);
  }

  onExploreResults(chartClickResponse: ChartClickResponse) {
    this.onChartClicked(chartClickResponse);
  }

  onOpenCompareResultsHint() {
    const modalRef = this._modalService.open(CompareResultsInfoComponent, {
      centered: true,
      scrollable: true,
    });
  }

  onOpenSurveySummaries(model: SurveySummaryBasicModel) {
    if (!this.shouldShowSelfRegistration$) return;
    if (!this.selfSurveys$) return;
    this._openSurveySummariesSubscription =
      combineLatest([this.shouldShowSelfRegistration$, this.selfSurveys$])
        .pipe(
          take(1),
          map((combo) => {
            const shouldShowSelfRegistration = combo[0];
            const selfSurveys = combo[1];
            const result = {
              hasIncompleteSurveys: false,
              firstIncompleteSurveyMoniker: '',
              shouldShowSelfRegistration
            }
            const incompleteSurveys = selfSurveys?.filter(ss => ss.status < SurveyStatuses.completeSurvey && moment(ss.lockDate).isAfter(moment()));
            const incompleteSurveyCount = selfSurveys?.filter(ss => ss.status < SurveyStatuses.completeSurvey && moment(ss.lockDate).isAfter(moment()))?.length ?? 0;
            result.hasIncompleteSurveys = incompleteSurveyCount > 0;
            if (incompleteSurveys && incompleteSurveyCount > 0) {
              result.firstIncompleteSurveyMoniker = incompleteSurveys[0].moniker;
            }
            return result;
          }),
          switchMap(({ hasIncompleteSurveys, firstIncompleteSurveyMoniker, shouldShowSelfRegistration }) => {
            return this._bottomSheet.open<SurveySummaryBottomSheetComponent, SurveySummaryBasicModel, SurveySummariesBottomSheetOutput>(SurveySummaryBottomSheetComponent, {
              data: { ...model, hasIncompleteSurveys, firstIncompleteSurveyMoniker, shouldShowSelfRegistration },
              panelClass: 'custom-bottom-sheet',
            }).afterDismissed().pipe(
              map((result) => {
                return { ...result, hasIncompleteSurveys, firstIncompleteSurveyMoniker };
              }),
            )
          })
        ).subscribe(({ firstIncompleteSurveyMoniker, hasIncompleteSurveys, type, surveyId }) => {
          switch (type) {
            case 'register':
              this.onRegisterClicked({ hasIncompleteSurveys, firstIncompleteSurveyMoniker })
              break;
            case 'selected':
              this.onSurveyClick(surveyId!);
              break;
          }
        });
  }

  onRegisterClicked(registerDetails: { [id: string]: any }): void {
    if (registerDetails['hasIncompleteSurveys']) {
      this.alert = {
        show: true,
        style: 'danger',
        text: this._translate.instant('surveySelfRegistrationAlertIncompleteSurvey'),
        meta: registerDetails['firstIncompleteSurveyMoniker']
      } as AlertModel;
    } else {
      this._selfRegistrationService.openSelfRegistration();
    }
  }

  onAlertDismissed(): void {
    this.alert = null;
  }

  onAlertTextClicked(alertIdentifier: string): void {
    this.alert = null;
    if (alertIdentifier) {
      this._router.navigate(['/surveys', { id: alertIdentifier }]);
    }
    console.log(alertIdentifier);
  }

  private _getResponseTypeName(responseType: SurveyResponseTypes) {
    switch (responseType) {
      case SurveyResponseTypes.self:
        return 'self';
      case SurveyResponseTypes.benchmark:
        return 'benchmark';
      case SurveyResponseTypes.other:
        return 'other';
      default:
        throw new Error('survey response type is invalid');
    }
  }
}
